import { useMemo, useState } from 'react';
import {
  ChevronLeft, ChevronRight, DollarSign, Package, CalendarCheck,
  TrendingUp, PackageOpen,
} from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { formatCurrency } from '../../shared/models';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EMPTY_DAY = { totalEarnings: 0, deliveryCount: 0, deliveryFees: 0, tips: 0, totalDistance: 0 };

const startOfDay = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const startOfMonth = d => new Date(d.getFullYear(), d.getMonth(), 1);
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Monthly calendar mirroring iOS DriverDailyEarningsView: month nav, day cells
// with earnings + delivery count, tap → day detail sheet, monthly summary.
export default function DailyEarningsCalendar({ orders }) {
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);

  // Group orders into per-day earnings for the displayed month.
  const earningsByDay = useMemo(() => {
    const mStart = startOfMonth(monthCursor);
    const mEnd = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1);
    const map = new Map();
    for (const o of orders) {
      if (!o.date || o.date < mStart || o.date >= mEnd) continue;
      const key = startOfDay(o.date).getTime();
      const g = map.get(key) || { ...EMPTY_DAY, date: startOfDay(o.date) };
      g.totalEarnings += o.totalEarnings;
      g.deliveryCount += 1;
      g.deliveryFees += o.deliveryFee;
      g.tips += o.tipAmount;
      g.totalDistance += o.distance;
      map.set(key, g);
    }
    return map;
  }, [orders, monthCursor]);

  const daysInMonth = useMemo(() => {
    const mStart = startOfMonth(monthCursor);
    const days = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < mStart.getDay(); i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      cells.push(new Date(monthCursor.getFullYear(), monthCursor.getMonth(), d));
    }
    return cells;
  }, [monthCursor]);

  const summary = useMemo(() => {
    let total = 0, deliveries = 0, days = 0;
    for (const g of earningsByDay.values()) {
      total += g.totalEarnings;
      deliveries += g.deliveryCount;
      if (g.deliveryCount > 0) days += 1;
    }
    return { total, deliveries, days, avgDaily: days > 0 ? total / days : 0 };
  }, [earningsByDay]);

  const isCurrentMonth =
    monthCursor.getFullYear() === new Date().getFullYear() &&
    monthCursor.getMonth() === new Date().getMonth();

  const earningsFor = date =>
    date ? (earningsByDay.get(startOfDay(date).getTime()) || { ...EMPTY_DAY, date }) : EMPTY_DAY;

  const monthLabel = monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = new Date();

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-gray-900 px-1">Daily Earnings</h3>

      {/* Month selector */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setMonthCursor(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
          className="p-1.5 text-blue-500 active:bg-blue-50 rounded-full"
          aria-label="Previous month"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-lg font-bold text-gray-900">{monthLabel}</span>
        <button
          onClick={() => setMonthCursor(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
          disabled={isCurrentMonth}
          className="p-1.5 text-blue-500 active:bg-blue-50 rounded-full disabled:opacity-30"
          aria-label="Next month"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 space-y-2">
        <div className="grid grid-cols-7 gap-1.5">
          {WEEKDAYS.map(d => (
            <div key={d} className="text-center text-[11px] font-semibold text-gray-400">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {daysInMonth.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} className="h-[62px]" />;
            const e = earningsFor(date);
            const hasEarnings = e.totalEarnings > 0;
            const isToday = sameDay(date, today);
            const isSelected = sameDay(date, selectedDate);
            let cellCls = 'bg-gray-100 text-gray-800';
            if (isSelected) cellCls = 'bg-[#FF6B35] text-white';
            else if (isToday) cellCls = 'bg-blue-500 text-white';
            else if (hasEarnings) cellCls = 'bg-green-500/10 text-gray-800';
            return (
              <button
                key={date.getTime()}
                onClick={() => setSelectedDate(date)}
                className={`h-[62px] rounded-lg flex flex-col items-center justify-center gap-0.5 transition-colors ${cellCls} ${
                  isSelected ? 'ring-2 ring-[#FF6B35]' : isToday ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <span className={`text-sm ${isToday || isSelected ? 'font-bold' : ''}`}>
                  {date.getDate()}
                </span>
                {hasEarnings && (
                  <>
                    <span className={`text-[10px] leading-none ${isToday || isSelected ? 'text-white' : 'text-green-600'}`}>
                      {formatCurrency(e.totalEarnings)}
                    </span>
                    <span className={`text-[9px] leading-none ${isToday || isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                      {e.deliveryCount}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Monthly summary */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
        <h4 className="font-bold text-gray-900">Monthly Summary</h4>
        <div className="grid grid-cols-2 gap-3">
          <SummaryCard title="Total Earnings" value={formatCurrency(summary.total)} Icon={DollarSign} iconCls="text-green-500" />
          <SummaryCard title="Deliveries" value={`${summary.deliveries}`} Icon={Package} iconCls="text-blue-500" />
          <SummaryCard title="Days Active" value={`${summary.days}`} Icon={CalendarCheck} iconCls="text-orange-500" />
          <SummaryCard title="Avg Daily" value={formatCurrency(summary.avgDaily)} Icon={TrendingUp} iconCls="text-purple-500" />
        </div>
      </div>

      {/* Day detail sheet */}
      <Sheet
        open={!!selectedDate}
        onClose={() => setSelectedDate(null)}
        title="Daily Details"
      >
        {selectedDate && <DayDetail date={selectedDate} earnings={earningsFor(selectedDate)} />}
      </Sheet>
    </div>
  );
}

function SummaryCard({ title, value, Icon, iconCls }) {
  return (
    <div className="rounded-xl bg-gray-50 p-3 space-y-1.5">
      <Icon size={20} className={iconCls} />
      <div className="text-xl font-extrabold text-gray-900">{value}</div>
      <div className="text-[11px] text-gray-400">{title}</div>
    </div>
  );
}

// Mirrors iOS DriverDayEarningsDetailSheet.
function DayDetail({ date, earnings }) {
  const avg = earnings.deliveryCount > 0 ? earnings.totalEarnings / earnings.deliveryCount : 0;
  return (
    <div className="px-4 pb-6 space-y-5">
      <div className="text-center space-y-1 py-2">
        <p className="text-xl font-bold text-gray-900">
          {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
        <p className="text-sm text-gray-400">Daily Earnings</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-gray-100 p-4 text-center">
          <p className="text-[11px] text-gray-400">Total Earnings</p>
          <p className="text-2xl font-extrabold text-green-600">{formatCurrency(earnings.totalEarnings)}</p>
        </div>
        <div className="rounded-xl bg-gray-100 p-4 text-center">
          <p className="text-[11px] text-gray-400">Deliveries</p>
          <p className="text-2xl font-extrabold text-blue-500">{earnings.deliveryCount}</p>
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="font-bold text-gray-900">Earnings Breakdown</h4>
        <div className="rounded-xl bg-gray-50 p-4 space-y-2">
          <BreakdownRow label="Delivery Fees" value={earnings.deliveryFees * 0.9} />
          <BreakdownRow label="Tips" value={earnings.tips} />
          <div className="border-t border-gray-200 my-1" />
          <div className="flex justify-between">
            <span className="font-bold text-gray-900">Total</span>
            <span className="font-bold text-green-600">{formatCurrency(earnings.totalEarnings)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="font-bold text-gray-900">Statistics</h4>
        <div className="rounded-xl bg-gray-50 p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Average per Delivery</span>
            <span className="text-sm font-medium text-gray-800">{formatCurrency(avg)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Total Distance</span>
            <span className="text-sm font-medium text-gray-800">{earnings.totalDistance.toFixed(1)} miles</span>
          </div>
        </div>
      </div>

      {earnings.deliveryCount === 0 && (
        <div className="flex flex-col items-center py-8 gap-3 text-gray-400">
          <PackageOpen size={40} />
          <p className="text-sm">No deliveries for this day</p>
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-800">{formatCurrency(value)}</span>
    </div>
  );
}
